import React, { useMemo } from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import type { BenchmarkCardConfig, BenchmarkComparisonCardData } from "store/types";

const BAR_COLORS: Record<string, string> = {
  portfolio: "#1A3C34",
  peer: "#2E8B57",
  market: "#88D5CA"
};

interface Props {
  config: BenchmarkCardConfig;
  data?: BenchmarkComparisonCardData;
  loading?: boolean;
}

export const BenchmarkComparisonCard: React.FC<Props> = ({ config, data, loading }) => {
  const hasNoBenchmarks = !config.benchmarkIds || config.benchmarkIds.length === 0;
  const hasData = data && data.length > 0;

  const options: Highcharts.Options = useMemo(() => {
    if (!data || data.length === 0) return {};

    const categories = data.map((d) => d.name);
    const values = data.map((d) => d.value);
    const colors = data.map((d) => BAR_COLORS[d.type] ?? "#9CA3AF");
    const postfix = data[0]?.postfix ?? "%";

    return {
      chart: {
        type: "column",
        height: 220,
        marginTop: 16,
        marginRight: 16,
        marginLeft: 50,
        marginBottom: 48,
        style: { fontFamily: "inherit" }
      },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        categories,
        labels: {
          style: { fontSize: "10px", color: "#6B7280" },
          rotation: categories.length > 5 ? -45 : 0
        },
        lineColor: "#E5E7EB",
        tickColor: "transparent"
      },
      yAxis: {
        title: { text: null },
        labels: {
          formatter() {
            return `${(this.value as number).toFixed(1)}${postfix}`;
          },
          style: { fontSize: "10px", color: "#9CA3AF" }
        },
        gridLineColor: "#F3F4F6"
      },
      tooltip: {
        formatter() {
          return `<b>${this.x}</b><br/>IRR: ${(this.y ?? 0).toFixed(2)}${postfix}`;
        }
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          borderRadius: 3,
          colorByPoint: true,
          colors
        }
      },
      legend: { enabled: false },
      series: [
        {
          type: "column",
          name: "IRR",
          data: values
        }
      ]
    };
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ p: "16px 12px 12px" }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 1.5
          }}
        >
          IRR Benchmark Comparison
        </Typography>
        <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  if (hasNoBenchmarks) {
    return (
      <Box sx={{ p: "16px 12px 12px" }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 1.5
          }}
        >
          IRR Benchmark Comparison
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#9CA3AF", mt: 2 }}>
          Select benchmarks in settings
        </Typography>
      </Box>
    );
  }

  if (!hasData) {
    return (
      <Box sx={{ p: "16px 12px 12px" }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 1.5
          }}
        >
          IRR Benchmark Comparison
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#9CA3AF", mt: 2 }}>
          No benchmark data available
        </Typography>
      </Box>
    );
  }

  // Check for missing benchmarks
  const respondedNames = new Set(data.filter((d) => d.type !== "portfolio").map((d) => d.name));
  const missingCount = config.benchmarkIds.length - respondedNames.size;

  return (
    <Box sx={{ p: "16px 12px 12px" }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#6B7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 0.5
        }}
      >
        IRR Benchmark Comparison
      </Typography>

      <HighchartsReact highcharts={Highcharts} options={options} />

      {missingCount > 0 && (
        <Typography sx={{ fontSize: 10, color: "#D97706", mt: 0.5 }}>
          {missingCount} benchmark{missingCount > 1 ? "s" : ""} returned no data
        </Typography>
      )}
    </Box>
  );
};
